import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Clock, BarChart2, RefreshCw } from 'lucide-react';

// Function to format timestamps nicely
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

// Function to format endpoint names for display
const formatEndpoint = (endpoint) => {
  // Remove /api/ prefix
  const simplified = endpoint.replace(/^\/api\//, '');
  // Split on slashes and capitalize words
  return simplified
    .split('/')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' / ');
};

// Component to render a loading spinner
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const ApiCounterDashboard = () => {
  const [data, setData] = useState([]);
  const [totalCalls, setTotalCalls] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sortBy, setSortBy] = useState('count'); // Default sort by count
  const [showAll, setShowAll] = useState(false); // Toggle to show all endpoints
  
  // Function to fetch data from the API
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/stats/counter');
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setData(result.data || []);
        setTotalCalls(result.total_calls || 0);
      } else {
        throw new Error(result.message || 'Failed to retrieve counter data');
      }
      
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Error fetching API counter data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch data on component mount
  useEffect(() => {
    fetchData();
    
    // Optional: Set up auto-refresh
    const intervalId = setInterval(fetchData, 60000); // Refresh every minute
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);
  
  // Sort and filter the data
  const processedData = React.useMemo(() => {
    // Filter out the counter endpoint itself
    let filteredData = data.filter(item => item.endpoint !== '/api/stats/counter');
    
    // Sort data based on the selected sort method
    if (sortBy === 'count') {
      filteredData.sort((a, b) => b.count - a.count);
    } else if (sortBy === 'endpoint') {
      filteredData.sort((a, b) => a.endpoint.localeCompare(b.endpoint));
    } else if (sortBy === 'recent') {
      filteredData.sort((a, b) => new Date(b.last_called) - new Date(a.last_called));
    }
    
    // Limit display to top 10 if not showing all
    return showAll ? filteredData : filteredData.slice(0, 10);
  }, [data, sortBy, showAll]);
  
  // Prepare data for the chart
  const chartData = React.useMemo(() => {
    return processedData.map(item => ({
      name: formatEndpoint(item.endpoint),
      calls: item.count
    })).reverse(); // Reverse to make the chart read from top to bottom
  }, [processedData]);
  
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading API Counter Data</h2>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 mb-6 bg-gray-50 rounded-lg shadow-lg p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-blue-600" /> 
            API Usage Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            {totalCalls.toLocaleString()} total API calls tracked
          </p>
        </div>
        
        <div className="mt-4 md:mt-0 flex items-center gap-2">
          <span className="text-sm text-gray-500 flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            Last updated: {lastUpdated ? formatTimestamp(lastUpdated) : 'Never'}
          </span>
          <button 
            onClick={fetchData} 
            className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
            aria-label="Refresh data"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="mt-6">
          <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div className="flex gap-2 mb-3 sm:mb-0">
              <button
                className={`px-3 py-1 rounded text-sm ${sortBy === 'count' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => setSortBy('count')}
              >
                Sort by Count
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${sortBy === 'recent' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => setSortBy('recent')}
              >
                Sort by Recent
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${sortBy === 'endpoint' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => setSortBy('endpoint')}
              >
                Sort by Name
              </button>
            </div>
            
            <button
              className="px-3 py-1 rounded text-sm bg-gray-200 text-gray-800 hover:bg-gray-300"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Show Top 10' : 'Show All'}
            </button>
          </div>
          
          {/* Chart view of the data */}
          <div className="mb-8 h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category"
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => [`${value} calls`, 'Usage']}
                  labelFormatter={(value) => `Endpoint: ${value}`}
                />
                <Legend />
                <Bar dataKey="calls" fill="#3b82f6" name="API Calls" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Table view of the data */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b">Endpoint</th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b">Call Count</th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b">Last Called</th>
                </tr>
              </thead>
              <tbody>
                {processedData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <span className="font-mono text-sm">{item.endpoint}</span>
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <span className="font-semibold">{item.count.toLocaleString()}</span>
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <span className="text-sm text-gray-600">{formatTimestamp(item.last_called)}</span>
                    </td>
                  </tr>
                ))}
                {processedData.length === 0 && (
                  <tr>
                    <td colSpan="3" className="py-4 px-4 text-center text-gray-500">
                      No API calls have been recorded yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {!showAll && data.length > 10 && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowAll(true)}
                className="text-blue-600 hover:underline"
              >
                Show all {data.length} endpoints
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiCounterDashboard;